import {
  CloseCircleOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppContext from "../../AppContext";
import axios from "axios";
import { BASE_URL } from "../../Constants";
import { message, Table } from "antd";
import Layout from "../Layout";
import { Content } from "antd/es/layout/layout";
import { toast } from "react-toastify";
import CardPreview from "../shared/CardPreview";

export const PublicEmployeesList = () => {
  const { t } = useTranslation();
  const [previewEmployee, setPreviewEmployee] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [loadingEmployeeId, setLoadingEmployeeId] = useState(null);

  const columns = [
    {
      title: t("form.firstName"),
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: t("form.lastName"),
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: t("form.email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("form.subType"),
      dataIndex: "isTrial",
      key: "isTrial",
      render: (_, record) => (
        <span
          className={`px-2 py-1 rounded text-white text-sm ${
            record.isTrial ? "bg-yellow-500" : "bg-green-500"
          }`}
        >
          {record.isTrial ? t("form.trial") : t("form.paid")}
        </span>
      ),
    },
    {
      title: t("form.expiratesOn"),
      dataIndex: "expirationDate",
      key: "expirationDate",
      render: (_, record) => {
        const date = new Date(record.expirationDate);
        const isExpired = date < new Date();

        return (
          <span
            className={`px-2 py-1 rounded text-sm ${
              isExpired
                ? "bg-red-100 text-red-600"
                : "bg-green-100 text-green-600"
            }`}
          >
            {date.toLocaleDateString("en-GB", {
              day: "numeric",
              month: "numeric",
              year: "numeric",
            })}
          </span>
        );
      },
      sorter: (a, b) => {
        const now = new Date();
        const aDate = new Date(a.expirationDate);
        const bDate = new Date(b.expirationDate);

        const aExpired = aDate < now;
        const bExpired = bDate < now;

        // Prioritize expired ones first
        if (aExpired && !bExpired) return -1;
        if (!aExpired && bExpired) return 1;

        // If both are expired or both are not expired, sort by date
        return aDate.getTime() - bDate.getTime();
      },
    },

    {
      title: t("table.admin.actions"),
      key: "actions",
      render: (_, record) => (
        <div className="space-x-2 flex">
          <button
            type="button"
            onClick={() => {
              setPreviewEmployee(record);
              setIsPreviewOpen(true);
              fetchEmployeeData(record.id);
            }}
            disabled={loadingEmployeeId !== null}
            className={`${
              loadingEmployeeId === record.id
                ? "bg-gray-400"
                : loadingEmployeeId !== null
                ? "bg-gray-300"
                : "bg-green-500 hover:bg-green-600"
            } text-white px-3 py-2 rounded flex items-center`}
            title={t("buttons.preview")}
          >
            {loadingEmployeeId === record.id ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            ) : (
              <EyeOutlined />
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/public-employee-form/${record.id}`)}
            disabled={loadingEmployeeId !== null}
            className={`${
              loadingEmployeeId !== null
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-indigo-500 hover:bg-indigo-600"
            } text-white px-3 py-2 rounded flex items-center`}
          >
            <EditOutlined />
          </button>
        </div>
      ),
    },
  ];

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [cardData, setCardData] = useState();
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const debouncedFetchEmployees = useCallback(
    debounce((searchValue) => {
      fetchEmployees(10, 0, searchValue);
    }, 500),
    []
  );

  const fetchEmployees = async (take = 10, skip = 0, searchParam = "") => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/PublicEmployee?take=${take}&skip=${skip}&searchParam=${searchParam}`
      );
      setEmployees(response.data.items);
      setFilteredData(response.data.items);
      setTotalRecords(response.data.totalRecords);
    } catch (error) {
      console.error("Error fetching employees:", error);
      message.error(t("messages.error.fetchCompany"));
    } finally {
      setLoading(false);
    }
  };

  const OnPageChange = (e) => {
    const take = e.pageSize;
    const skip = (e.current - 1) * e.pageSize;
    fetchEmployees(take, skip, searchText);
  };

  const onSearchChange = (e) => {
    setSearchText(e.target.value);
    debouncedFetchEmployees(e.target.value);
  }

  const fetchEmployeeData = async (employeeId) => {
    setLoadingEmployeeId(employeeId);
    try {
      const response = await axios.get(
        `${BASE_URL}/PublicEmployee/${employeeId}`
      );
      if (response.status === 200) {
        const { digitalCardConfig } = response.data;
        setCardData({
          companyLogoVisible: digitalCardConfig?.companyLogoVisible ?? false,
          profileImage: null,
          companyLogoUrl: response.data.iconUrl,
          profileImagePreview: response.data.profileUrl,
          profileImageVisible: digitalCardConfig?.profileImageVisible ?? true,
          backgroundImageUrl: digitalCardConfig?.backgroundImageUrl ?? null,
          backgroundImage: null,
          backgroundImagePreview: digitalCardConfig.backgroundImageUrl,
          name: response.data.firstName + " " + response.data.lastName,
          nameVisible: digitalCardConfig?.nameVisible ?? true,
          designation: response.data.designation,
          designationVisible: digitalCardConfig?.designationVisible ?? true,
          phone: response.data.phone,
          phoneVisible: digitalCardConfig?.phoneVisible ?? true,
          email: response.data.email,
          emailVisible: digitalCardConfig?.emailVisible ?? true,
          employeeId: response.data.employeeId,
          employeeIdVisible: digitalCardConfig?.employeeIdVisible ?? true,
          companyName: response.data.company,
          companyNameVisible: digitalCardConfig?.companyNameVisible ?? true,
          backgroundColor: digitalCardConfig?.backgroundColor ?? "#000147",
          useBackgroundImage: digitalCardConfig?.useBackgroundImage ?? false,
          qrCodeVisible: digitalCardConfig?.qrCodeVisible ?? true,
          dividerColor: digitalCardConfig?.dividerColor ?? "#e5e7eb",
          qrCodeUrl: response.data?.digitalID.qrCode ?? null,
          textStyles: {
            name: {
              color: digitalCardConfig?.textStyles?.name?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.name?.size ?? "18",
              bold: digitalCardConfig?.textStyles?.name?.bold ?? true,
              italic: digitalCardConfig?.textStyles?.name?.italic ?? false,
            },
            designation: {
              color:
                digitalCardConfig?.textStyles?.designation?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.designation?.size ?? "14",
              bold: digitalCardConfig?.textStyles?.designation?.bold ?? false,
              italic:
                digitalCardConfig?.textStyles?.designation?.italic ?? false,
            },
            phone: {
              color: digitalCardConfig?.textStyles?.phone?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.phone?.size ?? "12",
              bold: digitalCardConfig?.textStyles?.phone?.bold ?? false,
              italic: digitalCardConfig?.textStyles?.phone?.italic ?? false,
            },
            email: {
              color: digitalCardConfig?.textStyles?.email?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.email?.size ?? "12",
              bold: digitalCardConfig?.textStyles?.email?.bold ?? false,
              italic: digitalCardConfig?.textStyles?.email?.italic ?? false,
            },
            employeeId: {
              color:
                digitalCardConfig?.textStyles?.employeeId?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.employeeId?.size ?? "12",
              bold: digitalCardConfig?.textStyles?.employeeId?.bold ?? false,
              italic:
                digitalCardConfig?.textStyles?.employeeId?.italic ?? false,
            },
            companyName: {
              color:
                digitalCardConfig?.textStyles?.companyName?.color ?? "#ffffff",
              size: digitalCardConfig?.textStyles?.companyName?.size ?? "16",
              bold: digitalCardConfig?.textStyles?.companyName?.bold ?? false,
              italic:
                digitalCardConfig?.textStyles?.companyName?.italic ?? false,
            },
            titleColor: {
              color:
                digitalCardConfig?.textStyles?.titleColor?.color ?? "#ffffff",
            },
          },
        });
      }
    } catch (error) {
      console.error("Error fetching employee:", error);
      toast.error("Failed to load employee data");
    } finally {
      setLoadingEmployeeId(null);
    }
  };

  return (
    // <Layout className="min-h-full bg-transparent">
    <>
      <header className="py-4 bg-transparent">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-white">
            {t("navigation.publicEmployee")}
          </h1>
        </div>
      </header>
      <Content className="rounded-lg px-5 py-6 shadow bg-white sm:px-6">
        <div className="bg-white">
          <div className="flex justify-between items-center mb-6">
            <div className="relative">
              <SearchOutlined className="absolute top-2 left-2 text-gray-400" />
              <input
                type="text"
                placeholder={t("placeholders.searchByNameEmail")}
                value={searchText}
                style={{ width: 300, paddingLeft: "30px" }}
                onChange={onSearchChange}
                className="border border-gray-300 p-2 rounded"
              />
              {searchText && (
                <CloseCircleOutlined
                  className="absolute top-2 right-2 text-gray-400 cursor-pointer"
                  onClick={() => setSearchText("")}
                />
              )}
            </div>
            {/* <div className="space-x-2">
              <button
                type="button"
                onClick={() =>
                  maxEmployees <= employees.length
                    ? toast.error("Maximum number of employees reached")
                    : navigate("/employee-form")
                }
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-1.5 rounded-lg flex items-center"
              >
                <PlusOutlined className="mr-2" />
                {t("buttons.new")}
                <span className="lg:inline-block hidden ml-1">
                  {t("navigation.employee")}
                </span>
              </button>
            </div> */}
          </div>
          <Table
            columns={columns}
            dataSource={filteredData}
            scroll={{ x: true }}
            rowKey="id"
            loading={loading}
            onChange={OnPageChange}
            className="[&_.ant-table-thead_.ant-table-cell]:bg-gray-100 [&_.ant-table-thead_.ant-table-cell]:text-black"
            rowClassName={(record, index) =>
              `${
                index % 2 === 0 ? "bg-white" : "bg-gray-50"
              } hover:bg-gray-100 transition-colors`
            }
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              total: totalRecords,
            }}
          />
        </div>
      </Content>

      {/* Card Preview Modal */}
      {previewEmployee && loadingEmployeeId === null && (
        <CardPreview
          id={previewEmployee.id}
          name={previewEmployee.firstName + " " + previewEmployee.lastName}
          cardData={cardData}
          employee={previewEmployee}
          isIndividual={true}
          isOpen={isPreviewOpen}
          loading={false}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewEmployee(null);
            setLoadingEmployeeId(null);
          }}
        />
      )}
    </>
    // </Layout>
  );
};
